/**
 * DownloadApp — phone/tablet install for LGL Portal (Android APK + iOS PWA).
 */

import React, { useEffect, useState } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import {
  FIELD_APK_NAME,
  TEAM_APK_URL,
  isAndroidPhone,
  isIosDevice,
  resolveFieldApkHref,
} from '../mobile/progress/installPrompt';

const DownloadApp = () => {
  const { isMobile } = useResponsive();
  const [showIosInstructions, setShowIosInstructions] = useState(false);
  const [apkHref, setApkHref] = useState(null);
  const [apkOk, setApkOk] = useState(null);
  const android = isAndroidPhone();
  const ios = isIosDevice();

  useEffect(() => {
    let cancelled = false;
    resolveFieldApkHref()
      .then((href) => {
        if (cancelled) return;
        setApkHref(href);
        setApkOk(Boolean(href));
      })
      .catch(() => {
        if (!cancelled) setApkOk(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleAndroidDownload = () => {
    if (!apkHref) return;
    const link = document.createElement('a');
    link.href = apkHref;
    link.download = FIELD_APK_NAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const cardStyle = {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: isMobile ? '20px' : '30px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    border: '1px solid #e9ecef',
    textAlign: 'center',
    transition: 'transform 0.2s, box-shadow 0.2s',
    minWidth: isMobile ? '100%' : '280px',
    maxWidth: '350px',
  };

  return (
    <div style={{ padding: isMobile ? '10px' : '20px' }}>
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h2 style={{
          margin: '0 0 10px 0',
          color: '#333',
          fontSize: isMobile ? '1.5rem' : '2rem',
        }}>
          Download LGL Portal
        </h2>
        <p style={{
          color: '#666',
          margin: 0,
          fontSize: isMobile ? '14px' : '16px',
        }}>
          Install on your phone or tablet. Use the website on a laptop.
        </p>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: '24px',
        justifyContent: 'center',
        alignItems: 'stretch',
        flexWrap: 'wrap',
      }}>
        {/* Android */}
        <div style={{
          ...cardStyle,
          border: android ? '2px solid #3DDC84' : cardStyle.border,
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🤖</div>
          <h3 style={{ margin: '0 0 8px 0', color: '#333', fontSize: '1.3rem' }}>
            Android
          </h3>
          <p style={{ color: '#666', margin: '0 0 20px 0', fontSize: '14px' }}>
            Download the LGL Portal APK and install it on this device.
          </p>
          <button
            type="button"
            onClick={handleAndroidDownload}
            disabled={apkOk === false}
            style={{
              backgroundColor: apkOk === false ? '#9ca3af' : '#3DDC84',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: apkOk === false ? 'not-allowed' : 'pointer',
              width: '100%',
            }}
          >
            {apkOk === false ? 'APK unavailable' : 'Download Android APK'}
          </button>
          <p style={{ color: '#999', margin: '16px 0 0 0', fontSize: '12px' }}>
            {apkOk === false
              ? 'Ask IT — the APK file is missing on this server.'
              : 'Version 1.0.3 — a new LGL Portal app (not an update of Field Progress). Install it, then delete the old Field Progress icon if it is still there. Allow Camera when Samsung asks.'}
          </p>
          <p style={{ color: '#444', margin: '16px 0 0 0', fontSize: '13px', wordBreak: 'break-all' }}>
            Team link:{' '}
            <a href={TEAM_APK_URL} download={FIELD_APK_NAME}>
              {TEAM_APK_URL}
            </a>
          </p>
        </div>

        {/* iOS */}
        <div style={{
          ...cardStyle,
          border: ios ? '2px solid #007AFF' : cardStyle.border,
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🍎</div>
          <h3 style={{ margin: '0 0 8px 0', color: '#333', fontSize: '1.3rem' }}>
            iPhone / iPad
          </h3>
          <p style={{ color: '#666', margin: '0 0 20px 0', fontSize: '14px' }}>
            Safari Share → Add to Home Screen (no App Store build yet).
          </p>
          <button
            type="button"
            onClick={() => setShowIosInstructions(true)}
            style={{
              backgroundColor: '#007AFF',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              width: '100%',
            }}
          >
            How to install on iOS
          </button>
          <p style={{ color: '#999', margin: '16px 0 0 0', fontSize: '12px' }}>
            Use Safari on this iPhone or iPad — Chrome on iOS cannot add to Home Screen.
          </p>
        </div>
      </div>

      <div style={{
        marginTop: '40px',
        padding: isMobile ? '20px' : '30px',
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        maxWidth: '700px',
        marginLeft: 'auto',
        marginRight: 'auto',
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#333', fontSize: '1.1rem' }}>
          How to install on Android
        </h3>
        <ol style={{
          margin: 0,
          paddingLeft: '20px',
          color: '#555',
          lineHeight: '1.8',
        }}>
          <li>Tap <strong>Download Android APK</strong></li>
          <li>Open the file from Notifications or Downloads</li>
          <li>Allow install from this source if Samsung asks</li>
          <li>Tap <strong>Install</strong>, then open <strong>LGL Portal</strong></li>
          <li>Sign in with your Microsoft account</li>
        </ol>
      </div>

      {showIosInstructions ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Install on iPhone or iPad"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px',
          }}
          onClick={() => setShowIosInstructions(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: isMobile ? '24px' : '32px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
            }}>
              <h2 style={{ margin: 0, color: '#333', fontSize: '1.4rem' }}>
                Install on iPhone / iPad
              </h2>
              <button
                type="button"
                onClick={() => setShowIosInstructions(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#999',
                }}
              >
                ×
              </button>
            </div>
            <ol style={{ margin: 0, paddingLeft: '24px', lineHeight: '2', color: '#444' }}>
              <li>Open this portal in <strong>Safari</strong></li>
              <li>Tap the <strong>Share</strong> button</li>
              <li>Tap <strong>Add to Home Screen</strong></li>
              <li>Tap <strong>Add</strong></li>
              <li>Open <strong>LGL Portal</strong> from the home screen and sign in</li>
            </ol>
            <button
              type="button"
              onClick={() => setShowIosInstructions(false)}
              style={{
                marginTop: 24,
                width: '100%',
                minHeight: 48,
                border: 0,
                borderRadius: 10,
                background: '#007AFF',
                color: '#fff',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Got it
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DownloadApp;
